import { useEffect, useMemo, useState } from "react";

import { Eye, EyeOff } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { getSupabaseClient, getSupabaseConfig, getSupabaseStorageKeyFromDirectUrl } from "@/lib/supabase/client";
import {
  bypassConfirmEmail,
  devSignInViaServer,
  devSignOutLocal,
  requestPasswordResetViaServer,
  resendSignupConfirmationViaServer,
  signInWithPasswordViaServer,
  signOut,
  signUpWithPassword,
  signUpWithPasswordViaServer,
  verifyEmailCodeViaServer,
} from "@/lib/supabase/auth";
import { upsertMyProfile } from "@/lib/supabase/profile";
import type { KarmaUser } from "@/lib/karma/types";
import { computeKarmaNumerology, parseBirthDate } from "@/lib/karma/numerology";
import { useSupabaseSession } from "@/hooks/use-supabase-session";

type ProfileSeed = Pick<KarmaUser, "handle" | "displayName">;

type SignupPayload = {
  firstName: string;
  lastName: string;
  birthDay: string;
  birthMonth: string;
  birthYear: string;
  gender: "kadin" | "erkek" | "non_binary" | "belirtmek_istemiyorum" | "";
};

function panelColorForDestiny(destinyNumber: number) {
  const map: Record<number, string> = {
    1: "#3b82f6",
    2: "#8b5cf6",
    3: "#f97316",
    4: "#22c55e",
    5: "#06b6d4",
    6: "#ec4899",
    7: "#3713ec",
    8: "#f59e0b",
    9: "#a855f7",
    11: "#3713ec",
    22: "#3713ec",
    33: "#3713ec",
  };
  return map[destinyNumber] ?? "#3713ec";
}

function setPanelPrimaryColor(color: string) {
  try {
    window.localStorage.setItem("panel_primary", color);
  } catch {
    // ignore
  }
}

function FieldLabel({ children }: { children: string }) {
  return <div className="block text-sm font-medium leading-tight text-slate-600 dark:text-slate-300">{children}</div>;
}

function withTimeout<T>(promise: PromiseLike<T>, ms: number) {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;

  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutId = setTimeout(() => {
      reject(new Error("Giriş isteği zaman aşımına uğradı. Sayfayı 1 kez yenileyip tekrar dene."));
    }, ms);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutId) clearTimeout(timeoutId);
  });
}

function profileSeedFromIdentity(identity: { email?: string | null; user_metadata?: Record<string, unknown> | null } | null): ProfileSeed {
  const metadata = ((identity?.user_metadata ?? null) as Record<string, unknown> | null) ?? {};
  const first = typeof metadata.first_name === "string" ? metadata.first_name.trim() : "";
  const last = typeof metadata.last_name === "string" ? metadata.last_name.trim() : "";
  const fullName = `${first} ${last}`.trim();
  const fullNameAlt = typeof metadata.full_name === "string" ? metadata.full_name.trim() : "";
  const nameAlt = typeof metadata.name === "string" ? metadata.name.trim() : "";
  const displayNameAlt = typeof metadata.display_name === "string" ? metadata.display_name.trim() : "";
  const email = typeof identity?.email === "string" ? identity.email.trim().toLowerCase() : "";
  const emailHandle = email ? email.split("@")[0] : "";

  return {
    displayName: fullName || fullNameAlt || nameAlt || displayNameAlt || emailHandle || "Kullanıcı",
    handle: emailHandle || "user",
  };
}

export function SupabaseAuthCard({ me, className, onLoginSuccess }: { me: ProfileSeed; className?: string; onLoginSuccess?: () => void }) {
  const { toast } = useToast();
  const { user, loading, error } = useSupabaseSession();

  const [mode, setMode] = useState<"signin" | "signup" | "forgot" | "reset">("signin");

  const bypassEnabled = useMemo(() => {
    try {
      return String((globalThis as any)?.__ENV__?.ALLOW_EMAIL_BYPASS ?? "").trim() === "1";
    } catch {
      return false;
    }
  }, []);

  const [email, setEmail] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [unconfirmedEmail, setUnconfirmedEmail] = useState<string | null>(null);
  const [sessionOk, setSessionOk] = useState(false);
  const [devDiag, setDevDiag] = useState<string>("");
  const [showDevDiag, setShowDevDiag] = useState(false);

  const [resetPassword, setResetPassword] = useState("");
  const [resetConfirmPassword, setResetConfirmPassword] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showResetConfirmPassword, setShowResetConfirmPassword] = useState(false);

  const [signup, setSignup] = useState<SignupPayload>({
    firstName: "",
    lastName: "",
    birthDay: "",
    birthMonth: "",
    birthYear: "",
    gender: "",
  });

  const signupDisplayName = useMemo(() => {
    return `${signup.firstName} ${signup.lastName}`.trim();
  }, [signup.firstName, signup.lastName]);

  useEffect(() => {
    if (!import.meta.env.DEV) return;
    try {
      const enabled = (globalThis.localStorage?.getItem("panel_dev_diag") ?? "") === "1";
      setShowDevDiag(enabled);
    } catch {
      setShowDevDiag(false);
    }
  }, []);

  useEffect(() => {
    try {
      const saved = globalThis.localStorage?.getItem("panel_saved_email") ?? "";
      const remember = (globalThis.localStorage?.getItem("panel_remember_me") ?? "1") !== "0";
      setRememberMe(remember);
      if (remember && saved && !email) setEmail(saved);
    } catch {
      // ignore
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // If the user clicks a verification link from email, verify automatically.
  useEffect(() => {
    try {
      const href = globalThis.location?.href ?? "";
      if (!href) return;

      const url = new URL(href);
      const linkEmail = (url.searchParams.get("verify_email") ?? "").trim().toLowerCase();
      const linkCode = (url.searchParams.get("verify_code") ?? "").trim();
      if (!linkEmail || !linkCode) return;

      setEmail(linkEmail);
      setMode("signin");
      setUnconfirmedEmail(linkEmail);

      setBusy(true);
      void (async () => {
        try {
          await verifyEmailCodeViaServer(linkEmail, linkCode);
          toast({ title: "Doğrulandı", description: "E-posta doğrulandı. Şifren ile giriş yapabilirsin." });
          setUnconfirmedEmail(null);
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Doğrulama başarısız";
          toast({ title: "Doğrulama başarısız", description: msg });
        } finally {
          setBusy(false);
          try {
            const nextUrl = new URL(globalThis.location?.href ?? "");
            nextUrl.searchParams.delete("verify_email");
            nextUrl.searchParams.delete("verify_code");
            globalThis.history?.replaceState({}, "", `${nextUrl.pathname}${nextUrl.search}${nextUrl.hash}`);
          } catch {
            // ignore
          }
        }
      })();
    } catch {
      // ignore
    }
  }, [toast]);

  // Handle Supabase password recovery links (custom email flow):
  // /panel#access_token=...&refresh_token=...&type=recovery
  useEffect(() => {
    try {
      const hash = globalThis.location?.hash ?? "";
      if (!hash || !hash.startsWith("#")) return;

      const params = new URLSearchParams(hash.slice(1));
      const type = (params.get("type") ?? "").trim();
      if (type !== "recovery") return;

      const access_token = (params.get("access_token") ?? "").trim();
      const refresh_token = (params.get("refresh_token") ?? "").trim();
      if (!access_token || !refresh_token) return;

      setBusy(true);
      void (async () => {
        try {
          // Ensure proxy flag is only used in embedded preview contexts.
          try {
            const embedded = (() => {
              try {
                return globalThis.top !== globalThis.self;
              } catch {
                return false;
              }
            })();
            if (embedded) {
              globalThis.localStorage?.setItem("supabase_use_proxy", "1");
              globalThis.localStorage?.removeItem("supabase_disable_proxy");
            } else {
              globalThis.localStorage?.removeItem("supabase_use_proxy");
            }
          } catch {
            // ignore
          }

          const supabase = getSupabaseClient();
          const setRes = await withTimeout(supabase.auth.setSession({ access_token, refresh_token } as any), 45000);
          if (setRes.error) throw setRes.error;

          setMode("reset");
          toast({ title: "Şifre sıfırlama", description: "Yeni şifreni belirleyebilirsin." });
        } catch (err) {
          const msg = err instanceof Error ? err.message : "Şifre sıfırlama linki geçersiz";
          toast({ title: "Şifre sıfırlama", description: msg });
          setMode("signin");
        } finally {
          setBusy(false);
          try {
            // Clean up the hash so tokens are not left in the URL.
            globalThis.history?.replaceState({}, "", globalThis.location?.pathname ?? "/panel");
          } catch {
            // ignore
          }
        }
      })();
    } catch {
      // ignore
    }
  }, [toast]);

  useEffect(() => {
    let mounted = true;

    if (!user) {
      setSessionOk(false);
      return;
    }

    try {
      const { directUrl } = getSupabaseConfig();
      const storageKey = getSupabaseStorageKeyFromDirectUrl(String(directUrl)) ?? "";

      let storedUserId = "";
      if (storageKey) {
        try {
          const raw = globalThis.localStorage?.getItem(storageKey) ?? "";
          const parsed = raw ? (JSON.parse(raw) as any) : null;
          storedUserId = String(parsed?.user?.id ?? "");
        } catch {
          // ignore
        }
      }

      const ok = Boolean(storedUserId) && storedUserId === String(user.id);
      if (!mounted) return;

      setSessionOk(ok);

      // If we only have dev-auth user (localStorage) but not a real Supabase session,
      // clear dev auth so the user can sign in properly.
      if (!ok) {
        try {
          const raw = globalThis.localStorage?.getItem("dev_auth_user") ?? "";
          if (raw) devSignOutLocal();
        } catch {
          // ignore
        }
      }
    } catch {
      if (!mounted) return;
      setSessionOk(false);
    }

    return () => {
      mounted = false;
    };
  }, [user?.id]);

  const syncProfile = async () => {
    if (!sessionOk) {
      toast({ title: "Supabase", description: "Profil senkronlamak için Supabase oturumu gerekli." });
      return;
    }

    try {
      await upsertMyProfile(profileSeedFromIdentity(user ? { email: user.email, user_metadata: (user.metadata ?? {}) as Record<string, unknown> } : null));
      toast({ title: "Profil", description: "Profil senkronlandı." });
    } catch (err) {
      const asAny = err as any;
      const msg =
        err instanceof Error && err.message
          ? err.message
          : typeof asAny?.message === "string" && asAny.message
            ? asAny.message
            : "Profil kaydedilemedi";
      toast({ title: "Supabase", description: msg });
    }
  };

  const onRequestPasswordReset = async () => {
    const cleanEmail = email.trim();
    if (!cleanEmail) {
      toast({ title: "Email gerekli", description: "Lütfen e-posta adresini yaz." });
      return;
    }

    setBusy(true);
    try {
      await withTimeout(requestPasswordResetViaServer(cleanEmail), 15000);
      toast({
        title: "Şifre sıfırlama maili gönderildi",
        description: "Eğer bu e-posta kayıtlıysa, şifre sıfırlama bağlantısı birazdan mailine gelecek. (Spam klasörü dahil)",
      });
      setMode("signin");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Şifre sıfırlama maili gönderilemedi";
      toast({ title: "Şifre sıfırlama", description: msg });
    } finally {
      setBusy(false);
    }
  };

  const onResetPassword = async () => {
    if (!resetPassword || resetPassword !== resetConfirmPassword) {
      toast({ title: "Şifre uyuşmuyor", description: "Lütfen şifreyi iki kez aynı gir." });
      return;
    }

    setBusy(true);
    try {
      const supabase = getSupabaseClient();
      const res = await withTimeout(supabase.auth.updateUser({ password: resetPassword }), 15000);
      if (res.error) throw res.error;

      toast({ title: "Şifre güncellendi", description: "Yeni şifren ile giriş yapabilirsin." });

      // For safety, end the recovery session.
      try {
        await signOut();
      } catch {
        // ignore
      }

      setMode("signin");
      setResetPassword("");
      setResetConfirmPassword("");
      setShowResetPassword(false);
      setShowResetConfirmPassword(false);
      setPassword("");
      setConfirmPassword("");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Şifre güncellenemedi";
      toast({ title: "Şifre sıfırlama", description: msg });
    } finally {
      setBusy(false);
    }
  };

  const onSignIn = async () => {
    const cleanEmail = email.trim();

    const errToMessage = (err: unknown) => {
      if (err instanceof Error && err.message) return err.message;
      if (typeof err === "string") return err;

      const asAny = err as any;
      const name = asAny?.name || asAny?.constructor?.name;
      const msg = typeof asAny?.message === "string" ? asAny.message : "";
      const status = asAny?.status;
      const code = asAny?.code;
      const error = asAny?.error;
      const errorDescription = asAny?.error_description;

      const parts = [
        name ? `[${String(name)}]` : "",
        msg,
        status ? `status=${String(status)}` : "",
        code ? `code=${String(code)}` : "",
        error ? `error=${String(error)}` : "",
        errorDescription ? `detail=${String(errorDescription)}` : "",
      ].filter(Boolean);

      if (parts.length) return parts.join(" ");

      try {
        const keys = Object.getOwnPropertyNames(asAny ?? {});
        if (keys.length) {
          const kv = keys
            .slice(0, 8)
            .map((k) => `${k}=${String(asAny?.[k])}`)
            .join(" ");
          return kv || "Bilinmeyen hata";
        }
      } catch {
        // ignore
      }

      try {
        return JSON.stringify(err);
      } catch {
        return "Bilinmeyen hata";
      }
    };

    setBusy(true);
    setDevDiag("");

    const watchdogId = globalThis.setTimeout(() => {
      try {
        setBusy(false);
      } catch {
        // ignore
      }
      toast({
        title: "Giriş zaman aşımı",
        description: "İstek takıldı. Sayfayı 1 kez yenileyip tekrar dene.",
      });
    }, 25000);

    try {
      // Force proxy only in embedded environments.
      try {
        const embedded = (() => {
          try {
            return globalThis.top !== globalThis.self;
          } catch {
            return false;
          }
        })();
        if (embedded) {
          globalThis.localStorage?.setItem("supabase_use_proxy", "1");
          globalThis.localStorage?.removeItem("supabase_disable_proxy");
        } else {
          globalThis.localStorage?.removeItem("supabase_use_proxy");
        }
      } catch {
        // ignore
      }

      setDevDiag((p) => `${p}\n[server] /api/auth/signin...`);
      const tokens = await withTimeout(signInWithPasswordViaServer(cleanEmail, password), 20000);

      setDevDiag((p) => `${p}\n[server] persist session (storage)...`);

      // Write session to Supabase storage key directly to avoid setSession()/getSession() network
      // and recovery logic that can hang in preview/iframe environments.
      const { directUrl } = getSupabaseConfig();
      const storageKey = getSupabaseStorageKeyFromDirectUrl(String(directUrl)) ?? "";

      if (!storageKey) {
        throw new Error("Supabase storage anahtarı bulunamadı");
      }

      const now = Math.floor(Date.now() / 1000);
      const expiresIn = typeof (tokens as any)?.expires_in === "number" ? (tokens as any).expires_in : 3600;

      const tokenUser = (tokens as any).user ?? null;

      const fallbackUser = (() => {
        if (tokenUser?.id) return null;
        const accessToken = String((tokens as any)?.access_token ?? "");
        const parts = accessToken.split(".");
        if (parts.length < 2) return null;
        try {
          const payload = parts[1].replace(/-/g, "+").replace(/_/g, "/");
          const pad = payload.length % 4;
          const padded = pad ? payload + "=".repeat(4 - pad) : payload;
          const json = globalThis.atob ? globalThis.atob(padded) : "";
          const claims = json ? (JSON.parse(json) as any) : null;
          const sub = typeof claims?.sub === "string" ? claims.sub : "";
          if (!sub) return null;
          return {
            id: sub,
            email: typeof claims?.email === "string" ? claims.email : null,
            user_metadata: (claims?.user_metadata ?? {}) as any,
          };
        } catch {
          return null;
        }
      })();

      const sessionToStore = {
        access_token: (tokens as any).access_token,
        refresh_token: (tokens as any).refresh_token,
        token_type: (tokens as any).token_type ?? "bearer",
        expires_in: expiresIn,
        expires_at: now + expiresIn,
        user: tokenUser ?? fallbackUser,
      };

      globalThis.localStorage?.setItem(storageKey, JSON.stringify(sessionToStore));

      const persistedUserId = String((sessionToStore as any)?.user?.id ?? "").trim();
      if (persistedUserId) {
        globalThis.localStorage?.setItem("karma_last_auth_user_id", persistedUserId);
        globalThis.sessionStorage?.setItem("karma_store_session_user_id", persistedUserId);
      }

      globalThis.dispatchEvent?.(new Event("supabase-session-changed"));

      let storedUserId = "";
      try {
        const raw = globalThis.localStorage?.getItem(storageKey) ?? "";
        const parsed = raw ? (JSON.parse(raw) as any) : null;
        storedUserId = String(parsed?.user?.id ?? "");
      } catch {
        // ignore
      }

      const tokenUserId = String((tokens as any)?.user?.id ?? "");
      const sessionUserId = tokenUserId || storedUserId;

      setDevDiag((p) => `${p}\n[server] session user: ${sessionUserId || "(none)"}`);

      if (!sessionUserId) throw new Error("Oturum açılamadı.");

      setUnconfirmedEmail(null);

      try {
        if (rememberMe) {
          globalThis.localStorage?.setItem("panel_remember_me", "1");
          if (cleanEmail) globalThis.localStorage?.setItem("panel_saved_email", cleanEmail);
        } else {
          globalThis.localStorage?.setItem("panel_remember_me", "0");
          globalThis.localStorage?.removeItem("panel_saved_email");
        }
      } catch {
        // ignore
      }

      toast({ title: "Giriş başarılı", description: "Oturum açıldı." });

      // Notify parent component that login was successful
      onLoginSuccess?.();

      // Don't block sign-in if profile sync fails; it can be done later from the profile screen.
      void (async () => {
        try {
          const seedIdentity = (tokenUser ?? fallbackUser ?? null) as { email?: string | null; user_metadata?: Record<string, unknown> | null } | null;
          const seed = profileSeedFromIdentity(seedIdentity ?? { email: cleanEmail, user_metadata: null });
          await upsertMyProfile(seed);
        } catch {
          // ignore
        }
      })();
    } catch (err) {
      const msg = errToMessage(err) || "Giriş yapılamadı";
      const msgLower = msg.toLowerCase();

      const needsConfirm =
        Boolean((err as any)?.needsVerification) ||
        msgLower.includes("email not confirmed") ||
        msgLower.includes("not confirmed") ||
        msgLower.includes("doğrulan") ||
        msgLower.includes("verification") ||
        msgLower.includes("confirm");

      if (needsConfirm) {
        setUnconfirmedEmail(cleanEmail || null);
        toast({
          title: "E-posta doğrulaması gerekli",
          description: "Mail kutuna gelen doğrulama linkine tıklayıp hesabını aktif ettikten sonra giriş yapabilirsin.",
        });
        return;
      }

      setUnconfirmedEmail(null);
      toast({ title: "Giriş yapılamadı", description: msg });
    } finally {
      clearTimeout(watchdogId);
      setBusy(false);
    }
  };

  const onSignUp = async () => {
    const cleanEmail = email.trim();

    if (!signup.firstName.trim() || !signup.lastName.trim()) {
      toast({ title: "Eksik bilgi", description: "Lütfen isim ve soyisim gir." });
      return;
    }

    if (!signup.birthDay || !signup.birthMonth || !signup.birthYear) {
      toast({ title: "Eksik bilgi", description: "Lütfen doğum tarihi gir." });
      return;
    }

    if (!signup.gender) {
      toast({ title: "Eksik bilgi", description: "Lütfen cinsiyet seç." });
      return;
    }

    if (!password || password !== confirmPassword) {
      toast({ title: "Şifre uyuşmuyor", description: "Lütfen şifreyi iki kez aynı gir." });
      return;
    }

    const parsedBirth = parseBirthDate({
      day: Number(signup.birthDay || 0),
      month: Number(signup.birthMonth || 0),
      year: Number(signup.birthYear || 0),
    });

    setBusy(true);
    try {
      const numerology = computeKarmaNumerology(`${signup.firstName} ${signup.lastName}`, parsedBirth);
      const panelPrimary = panelColorForDestiny(numerology.destinyNumber);

      const redirectTo = (() => {
        try {
          let origin = globalThis.location?.origin ?? "";
          if (!origin || origin === "null" || !origin.startsWith("http")) {
            const href = globalThis.location?.href ?? "";
            if (href && href.startsWith("http")) origin = new URL(href).origin;
          }
          return origin ? `${origin}/panel` : undefined;
        } catch {
          return undefined;
        }
      })();

      const userMeta = {
        first_name: signup.firstName.trim(),
        last_name: signup.lastName.trim(),
        birth_day: parsedBirth.day,
        birth_month: parsedBirth.month,
        birth_year: parsedBirth.year,
        gender: signup.gender,
        preferred_gender: "herkes",
        destiny_number: numerology.destinyNumber,
        panel_primary: panelPrimary,
      } as const;

      // Prefer server-side signup (sends verification code via our SMTP).
      try {
        const serverRes = await withTimeout(signUpWithPasswordViaServer(cleanEmail, password, { data: userMeta as any }), 15000);

        setPanelPrimaryColor(panelPrimary);

        if ((serverRes as any)?.needsVerification) {
          setUnconfirmedEmail(cleanEmail);
          toast({
            title: "Kayıt alındı",
            description: "Doğrulama linki mail adresine gönderildi. Maildeki linke tıklayınca hesabın aktif olacak.",
          });

          setMode("signin");
          // Keep password so we can auto sign-in after verification.
          setConfirmPassword("");
          setShowPassword(false);
          setShowConfirmPassword(false);
          return;
        }

        // Backwards-compatible: if a session is returned, keep supporting it.
        const tokens = serverRes as any;
        if (tokens?.access_token && tokens?.refresh_token) {
          try {
            const embedded = (() => {
              try {
                return globalThis.top !== globalThis.self;
              } catch {
                return false;
              }
            })();
            if (embedded) {
              globalThis.localStorage?.setItem("supabase_use_proxy", "1");
              globalThis.localStorage?.removeItem("supabase_disable_proxy");
            } else {
              globalThis.localStorage?.removeItem("supabase_use_proxy");
            }
          } catch {
            // ignore
          }

          const supabase = getSupabaseClient();
          const setRes = await withTimeout(supabase.auth.setSession(tokens as any), 45000);
          if (setRes.error) throw setRes.error;

          setUnconfirmedEmail(null);
          toast({ title: "Kayıt başarılı", description: "Hesap oluşturuldu ve giriş yapıldı." });

          // Notify parent component that signup/login was successful
          onLoginSuccess?.();

          const handle = cleanEmail.split("@")[0] || "user";
          void upsertMyProfile({
            handle,
            displayName: signupDisplayName || handle,
            gender: signup.gender || undefined,
            prefPartnerGender: "herkes",
          });

          setMode("signin");
          setPassword("");
          setConfirmPassword("");
          setShowPassword(false);
          setShowConfirmPassword(false);
          return;
        }

        throw new Error("Sunucudan yanıt alınamadı");
      } catch (serverErr: any) {
        const status = typeof serverErr?.status === "number" ? serverErr.status : null;

        // If server-side auth isn't configured (dev/local), fall back to normal Supabase signup.
        if (status === 404) {
          // Fallback: normal Supabase signup (may send confirmation email).
          const res = await signUpWithPassword(cleanEmail, password, {
            data: userMeta as any,
            emailRedirectTo: redirectTo,
          });
          if (res.error) throw res.error;

          toast({ title: "Kayıt alındı", description: "Hesap oluşturuldu." });

          // If email confirmation is enabled, user won't be signed in yet.
          const needsConfirmation = !res.data?.session;
          if (needsConfirmation) {
            setUnconfirmedEmail(cleanEmail);
            toast({
              title: "E-posta doğrulaması",
              description: "Kayıt tamamlandı. Lütfen e-postana gelen doğrulama linkine tıkla.",
            });
          } else {
            setUnconfirmedEmail(null);
          }

          setMode("signin");
          setPassword("");
          setConfirmPassword("");
          setShowPassword(false);
          setShowConfirmPassword(false);

          const handle = cleanEmail.split("@")[0] || "user";
          try {
            await upsertMyProfile({
              handle,
              displayName: signupDisplayName || handle,
              gender: signup.gender || undefined,
              prefPartnerGender: "herkes",
            });
          } catch {
            // Email confirmation enabled -> no session yet. Profile sync can happen later after first sign-in.
          }
          return;
        }

        throw serverErr;
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Kayıt yapılamadı";
      const lower = String(msg).toLowerCase();

      const status = typeof (err as any)?.status === "number" ? (err as any).status : null;

      if (status === 409 || lower.includes("zaten kayıtlı")) {
        toast({
          title: "Bu e‑posta zaten kayıtlı",
          description: "Lütfen giriş yap. Şifreni unuttuysan ‘Şifremi unuttum’ ile sıfırlayabilirsin.",
        });
        setMode("signin");
        return;
      }

      const isSmtpError =
        lower.includes("smtp") ||
        lower.includes("smtp_host") ||
        lower.includes("smtp_user") ||
        lower.includes("smtp_pass") ||
        lower.includes("smtp ayarları");

      if (isSmtpError) {
        toast({
          title: "Mail gönderilemedi",
          description: "Mail sistemi geçici olarak çalışmıyor. Lütfen daha sonra tekrar dene.",
        });
      } else {
        toast({ title: "Kayıt yapılamadı", description: msg });
      }
    } finally {
      setBusy(false);
    }
  };

  const onSignOut = async () => {
    // If we're in dev-auth mode, signing out is just clearing local storage.
    try {
      const raw = globalThis.localStorage?.getItem("dev_auth_user") ?? "";
      if (raw) {
        devSignOutLocal();
        toast({ title: "Çıkış yapıldı", description: "Oturum kapatıldı." });
        return;
      }
    } catch {
      // ignore
    }

    setBusy(true);
    try {
      const res = await signOut();
      if (res.error) throw res.error;
      toast({ title: "Çıkış yapıldı", description: "Oturum kapatıldı." });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Çıkış yapılamadı";
      toast({ title: "Çıkış yapılamadı", description: msg });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      className={
        className
          ? `rounded-2xl border-slate-200/70 bg-white/80 p-6 shadow-sm backdrop-blur dark:border-[#3713ec]/15 dark:bg-slate-900/40 ${className}`
          : "rounded-2xl border-slate-200/70 bg-white/80 p-6 shadow-sm backdrop-blur dark:border-[#3713ec]/15 dark:bg-slate-900/40"
      }
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xl font-extrabold tracking-tight text-slate-900 dark:text-slate-100">Hesap</div>
          <div className="mt-1 text-sm text-slate-500 dark:text-slate-400">
            {error
              ? `Hata: ${error}`
              : loading
                ? "Kontrol ediliyor…"
                : user && sessionOk
                  ? `Giriş: ${user.email ?? user.id}`
                  : mode === "signup"
                    ? "Kayıt ol"
                    : mode === "forgot"
                      ? "Şifremi unuttum"
                      : mode === "reset"
                        ? "Şifre sıfırla"
                        : "Giriş yap"}
          </div>
        </div>

        {user && sessionOk ? (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              className="rounded-xl border-slate-200 bg-white/50 hover:bg-white dark:border-[#3713ec]/20 dark:bg-[#3713ec]/10 dark:hover:bg-[#3713ec]/15"
              onClick={onSignOut}
              disabled={busy}
            >
              Çıkış
            </Button>
          </div>
        ) : null}
      </div>

      {!user || !sessionOk ? (
        <form
          className="mt-5 flex w-full flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (busy) return;
            if (mode === "signup") void onSignUp();
            else if (mode === "forgot") void onRequestPasswordReset();
            else if (mode === "reset") void onResetPassword();
            else void onSignIn();
          }}
        >
          {/* Email */}
          <div className="flex w-full flex-col gap-2">
            <FieldLabel>Email</FieldLabel>
            <Input
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="ornek@mail.com"
              className="h-11 w-full rounded-xl border-slate-200 bg-white/70 focus-visible:ring-[#3713ec]/30 dark:border-[#3713ec]/15 dark:bg-[#3713ec]/10"
            />
          </div>

          {mode === "signup" ? (
            <>
              {/* Passwords */}
              <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex min-w-0 flex-col gap-2">
                  <FieldLabel>Şifre</FieldLabel>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      autoComplete="new-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-11 w-full rounded-xl border-slate-200 bg-white/70 pr-11 focus-visible:ring-[#3713ec]/30 dark:border-[#3713ec]/15 dark:bg-[#3713ec]/10"
                    />
                    <button
                      type="button"
                      aria-label={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 dark:text-slate-300"
                      onClick={() => setShowPassword((v) => !v)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex min-w-0 flex-col gap-2">
                  <FieldLabel>Şifre (tekrar)</FieldLabel>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      autoComplete="new-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="h-11 w-full rounded-xl border-slate-200 bg-white/70 pr-11 focus-visible:ring-[#3713ec]/30 dark:border-[#3713ec]/15 dark:bg-[#3713ec]/10"
                    />
                    <button
                      type="button"
                      aria-label={showConfirmPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 dark:text-slate-300"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>

              {/* Name */}
              <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex min-w-0 flex-col gap-2">
                  <FieldLabel>İsim</FieldLabel>
                  <Input
                    value={signup.firstName}
                    onChange={(e) => setSignup((s) => ({ ...s, firstName: e.target.value }))}
                    placeholder="Ad"
                    className="h-11 w-full rounded-xl border-slate-200 bg-white/70 focus-visible:ring-[#3713ec]/30 dark:border-[#3713ec]/15 dark:bg-[#3713ec]/10"
                  />
                </div>

                <div className="flex min-w-0 flex-col gap-2">
                  <FieldLabel>Soyisim</FieldLabel>
                  <Input
                    value={signup.lastName}
                    onChange={(e) => setSignup((s) => ({ ...s, lastName: e.target.value }))}
                    placeholder="Soyad"
                    className="h-11 w-full rounded-xl border-slate-200 bg-white/70 focus-visible:ring-[#3713ec]/30 dark:border-[#3713ec]/15 dark:bg-[#3713ec]/10"
                  />
                </div>
              </div>

              {/* Birthdate */}
              <div className="flex w-full flex-col gap-2">
                <FieldLabel>Doğum tarihi</FieldLabel>
                <div className="grid w-full grid-cols-3 gap-2">
                  <Input
                    value={signup.birthDay}
                    onChange={(e) => setSignup((s) => ({ ...s, birthDay: e.target.value }))}
                    inputMode="numeric"
                    placeholder="Gün"
                    className="h-11 w-full rounded-xl border-slate-200 bg-white/70 focus-visible:ring-[#3713ec]/30 dark:border-[#3713ec]/15 dark:bg-[#3713ec]/10"
                  />
                  <Input
                    value={signup.birthMonth}
                    onChange={(e) => setSignup((s) => ({ ...s, birthMonth: e.target.value }))}
                    inputMode="numeric"
                    placeholder="Ay"
                    className="h-11 w-full rounded-xl border-slate-200 bg-white/70 focus-visible:ring-[#3713ec]/30 dark:border-[#3713ec]/15 dark:bg-[#3713ec]/10"
                  />
                  <Input
                    value={signup.birthYear}
                    onChange={(e) => setSignup((s) => ({ ...s, birthYear: e.target.value }))}
                    inputMode="numeric"
                    placeholder="Yıl"
                    className="h-11 w-full rounded-xl border-slate-200 bg-white/70 focus-visible:ring-[#3713ec]/30 dark:border-[#3713ec]/15 dark:bg-[#3713ec]/10"
                  />
                </div>
              </div>

              <div className="flex w-full flex-col gap-2">
                <FieldLabel>Cinsiyet</FieldLabel>
                <select
                  value={signup.gender}
                  onChange={(e) => setSignup((s) => ({ ...s, gender: e.target.value as SignupPayload["gender"] }))}
                  className="h-11 w-full rounded-xl border border-slate-200 bg-white/70 px-3 text-sm text-slate-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3713ec]/30 dark:border-[#3713ec]/15 dark:bg-[#3713ec]/10 dark:text-slate-100"
                >
                  <option value="">Seçiniz</option>
                  <option value="kadin">Kadın</option>
                  <option value="erkek">Erkek</option>
                  <option value="non_binary">Non-binary</option>
                  <option value="belirtmek_istemiyorum">Belirtmek istemiyorum</option>
                </select>
              </div>
            </>
          ) : mode === "reset" ? (
            <>
              <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="flex min-w-0 flex-col gap-2">
                  <FieldLabel>Yeni şifre</FieldLabel>
                  <div className="relative">
                    <Input
                      type={showResetPassword ? "text" : "password"}
                      autoComplete="new-password"
                      value={resetPassword}
                      onChange={(e) => setResetPassword(e.target.value)}
                      className="h-11 w-full rounded-xl border-slate-200 bg-white/70 pr-11 focus-visible:ring-[#3713ec]/30 dark:border-[#3713ec]/15 dark:bg-[#3713ec]/10"
                    />
                    <button
                      type="button"
                      aria-label={showResetPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 dark:text-slate-300"
                      onClick={() => setShowResetPassword((v) => !v)}
                    >
                      {showResetPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div className="flex min-w-0 flex-col gap-2">
                  <FieldLabel>Yeni şifre (tekrar)</FieldLabel>
                  <div className="relative">
                    <Input
                      type={showResetConfirmPassword ? "text" : "password"}
                      autoComplete="new-password"
                      value={resetConfirmPassword}
                      onChange={(e) => setResetConfirmPassword(e.target.value)}
                      className="h-11 w-full rounded-xl border-slate-200 bg-white/70 pr-11 focus-visible:ring-[#3713ec]/30 dark:border-[#3713ec]/15 dark:bg-[#3713ec]/10"
                    />
                    <button
                      type="button"
                      aria-label={showResetConfirmPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 dark:text-slate-300"
                      onClick={() => setShowResetConfirmPassword((v) => !v)}
                    >
                      {showResetConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white/50 px-3 py-2 text-xs text-slate-700 dark:border-[#3713ec]/15 dark:bg-black/20 dark:text-slate-200">
                Yeni şifreni belirle ve kaydet.
              </div>
            </>
          ) : mode === "forgot" ? (
            <div className="rounded-xl border border-slate-200 bg-white/50 px-3 py-2 text-xs text-slate-700 dark:border-[#3713ec]/15 dark:bg-black/20 dark:text-slate-200">
              E‑posta adresini yaz. Eğer hesap varsa şifre sıfırlama linki mailine gönderilecek.
            </div>
          ) : (
            <div className="flex w-full flex-col gap-2">
              <FieldLabel>Şifre</FieldLabel>
              <div className="relative">
                <Input
                  type={showPassword ? "text" : "password"}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11 w-full rounded-xl border-slate-200 bg-white/70 pr-11 focus-visible:ring-[#3713ec]/30 dark:border-[#3713ec]/15 dark:bg-[#3713ec]/10"
                />
                <button
                  type="button"
                  aria-label={showPassword ? "Şifreyi gizle" : "Şifreyi göster"}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700 dark:text-slate-300"
                  onClick={() => setShowPassword((v) => !v)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>

              <label className="flex select-none items-center gap-2 text-xs text-slate-600 dark:text-slate-300">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-slate-300 text-[#3713ec]"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={busy}
                />
                Beni hatırla
              </label>

              <button
                type="button"
                className="self-start text-xs font-semibold text-[#3713ec] underline underline-offset-4"
                onClick={() => {
                  setUnconfirmedEmail(null);
                  setMode("forgot");
                }}
                disabled={busy}
              >
                Şifremi unuttum
              </button>

              {unconfirmedEmail ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-100">
                  <div className="font-semibold">E-posta doğrulaması gerekli.</div>
                  <div className="mt-1 text-[11px] opacity-90">Maildeki doğrulama linkine tıkla. Link otomatik olarak hesabını doğrulayacak.</div>

                  <div className="mt-3 grid gap-3">
                    <div className="flex flex-wrap gap-3">
                      <button
                        type="button"
                        className="font-semibold underline underline-offset-4"
                        disabled={busy}
                        onClick={async () => {
                          const redirectTo = (() => {
                            try {
                              let origin = globalThis.location?.origin ?? "";
                              if (!origin || origin === "null" || !origin.startsWith("http")) {
                                const href = globalThis.location?.href ?? "";
                                if (href && href.startsWith("http")) origin = new URL(href).origin;
                              }
                              return origin ? `${origin}/panel` : undefined;
                            } catch {
                              return undefined;
                            }
                          })();

                          setBusy(true);
                          try {
                            await resendSignupConfirmationViaServer(unconfirmedEmail, redirectTo);

                            toast({
                              title: "Doğrulama maili gönderildi",
                              description: "Lütfen mail kutunu (spam dahil) kontrol et.",
                            });
                          } catch (err) {
                            const msg = err instanceof Error ? err.message : "Mail gönderilemedi";
                            toast({ title: "Mail gönderilemedi", description: msg });
                          } finally {
                            setBusy(false);
                          }
                        }}
                      >
                        Doğrulama mailini tekrar gönder
                      </button>

                      {bypassEnabled ? (
                        <button
                          type="button"
                          className="font-semibold underline underline-offset-4"
                          disabled={busy}
                          onClick={async () => {
                            setBusy(true);
                            try {
                              await bypassConfirmEmail({ email: unconfirmedEmail });
                              toast({
                                title: "Doğrulama atlandı",
                                description: "Giriş tekrar deneniyor…",
                              });
                              await onSignIn();
                            } catch (err) {
                              const msg = err instanceof Error ? err.message : "İşlem başarısız";
                              const lower = String(msg).toLowerCase();

                              if (
                                lower.includes("service_role") ||
                                lower.includes("supabase admin config missing") ||
                                lower.includes("supabase server config missing")
                              ) {
                                toast({
                                  title: "İşlem başarısız",
                                  description:
                                    "Doğrulamayı atlamak için sunucuda SUPABASE_SERVICE_ROLE_KEY (ve SUPABASE_URL) ayarlı olmalı.",
                                });
                                return;
                              }

                              toast({ title: "İşlem başarısız", description: msg });
                            } finally {
                              setBusy(false);
                            }
                          }}
                        >
                          Doğrulamayı atla
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            <Button
              type="submit"
              className="h-11 rounded-xl bg-[#3713ec] px-6 text-white shadow-lg shadow-[#3713ec]/25 hover:bg-[#2f10d6]"
              disabled={busy}
            >
              {busy
                ? mode === "forgot"
                  ? "Gönderiliyor…"
                  : mode === "reset"
                    ? "Güncelleniyor…"
                    : "Bağlanılıyor…"
                : mode === "signup"
                  ? "Kayıt ol"
                  : mode === "forgot"
                    ? "Sıfırlama linki gönder"
                    : mode === "reset"
                      ? "Şifreyi güncelle"
                      : "Giriş"}
            </Button>

            <Button
              type="button"
              variant="outline"
              className="h-11 rounded-xl border-[#3713ec]/25 bg-white/40 px-6 text-[#3713ec] hover:bg-[#3713ec]/10 dark:bg-transparent dark:text-slate-100"
              onClick={() => {
                setUnconfirmedEmail(null);

                if (mode === "signup") setMode("signin");
                else if (mode === "signin") setMode("signup");
                else setMode("signin");
              }}
              disabled={busy}
            >
              {mode === "signup" ? "Giriş" : mode === "signin" ? "Kayıt ol" : "Girişe dön"}
            </Button>
          </div>

          {import.meta.env.DEV && showDevDiag && devDiag.trim() ? (
            <pre className="mt-4 whitespace-pre-wrap break-words rounded-xl border border-slate-200 bg-white/60 p-3 text-[11px] text-slate-700 dark:border-[#3713ec]/15 dark:bg-black/20 dark:text-slate-200">
              {devDiag.trim()}
            </pre>
          ) : null}

          <div className="text-xs text-slate-500 dark:text-slate-400">
            Tasarım Kodlama Ragıp Alkan|Digital Alchemist
          </div>
        </form>
      ) : null}
    </Card>
  );
}
